import React, { useEffect, useState } from 'react';
import FitTable from '../components/FitTable';
import PlayerName from '../components/PlayerName';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;

interface AverageScore {
  player_id: number;
  player_name: string;
  course_id: number;
  course_name: string;
  course_teebox_id: number;
  course_teebox_name: string;
  total_rounds: number;
  average_score: number;
}

interface HandicapIndex {
  player_id: number;
  first_name: string;
  last_name: string;
  handicap_index: number;
}

interface Teebox {
  course_teebox_id: string;
  course_id: string;
  course_teebox_name: string;
  course_teebox_slope: number;
  course_teebox_rating: number;
  course_teebox_yards: number;
  course_teebox_par: number;
}

interface Round {
  score_id: number;
  round_id: number;
  number_of_holes: number;
  date: string;
  course_name: string;
  course_teebox_par: number;
  course_teebox_name: string;
  course_teebox_slope: number;
  course_teebox_rating: number;
  player_score: number;
  handicap_differential: number;
}

interface PlayerData {
  player_id: number;
  player_name: string;
  rounds: Round[];
}

interface Course {
  course_id: string;
  course_name: string;
  facility_id: number | null;
  course_teebox_id: string;
  course_teebox_name: string;
  course_teebox_slope: number;
}

interface Facility {
  facility_id: number;
  facility_name: string;
  course_count?: number;
}

type GroupedScores = {
  [courseName: string]: {
    [teeboxName: string]: AverageScore[];
  };
};

const groupByCourseAndTeebox = (data: AverageScore[]): GroupedScores => {
  const grouped: GroupedScores = {};
  data.forEach((item) => {
    if (!grouped[item.course_name]) grouped[item.course_name] = {};
    if (!grouped[item.course_name][item.course_teebox_name]) grouped[item.course_name][item.course_teebox_name] = [];
    grouped[item.course_name][item.course_teebox_name].push(item);
  });
  return grouped;
};

const calculateTeeboxAverage = (players: AverageScore[]) => {
  const valid = players.map((p) => Number(p.average_score)).filter((s) => !isNaN(s));
  if (!valid.length) return '--';
  return (valid.reduce((sum, s) => sum + s, 0) / valid.length).toFixed(1);
};

// ── Handicap scoring window ──────────────────────────────────────────────────
// The handicap index is built from a player's 20 most-recent 18-hole rounds (the
// "scoring window"); of those, only the best few differentials are averaged. We
// mirror that selection here so the round history can show exactly which rounds
// feed the index and over what date span. Rounds arrive newest-first from
// /api/handicaps and carry the same `handicap_differential` the index query
// computes, so the picks line up with the backend. (Rounds tied to a still-active
// tournament are hidden from this endpoint, so in the rare case one falls inside
// the window the client view can lag the index until that tournament closes.)
const HOLES_FOR_INDEX = 18;
const WINDOW_SIZE = 20;

// How many of the best differentials are averaged for a given window size —
// mirrors the CASE expression in /api/handicap-index.
const bestCountForWindow = (n: number): number => {
  if (n >= 20) return 8;
  if (n === 19) return 7;
  if (n >= 17) return 6;
  if (n >= 15) return 5;
  if (n >= 12) return 4;
  if (n >= 9) return 3;
  if (n >= 6) return 2;
  if (n === 5) return 1;
  return 0;
};

interface HandicapWindow {
  windowIds: Set<number>;    // score_ids in the 20-round window
  countingIds: Set<number>;  // score_ids among the best-N actually averaged
  startDate: string | null;  // oldest date in the window
  endDate: string | null;    // newest date in the window
  windowCount: number;
  countingCount: number;
}

const parseLocalDate = (iso: string): Date => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const fmtShortDate = (iso: string | null): string =>
  iso ? parseLocalDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const computeHandicapWindow = (rounds: Round[]): HandicapWindow => {
  const eligible = rounds.filter((r) => Number(r.number_of_holes) === HOLES_FOR_INDEX);
  const window = eligible.slice(0, WINDOW_SIZE); // newest-first → most recent 20
  const n = window.length;
  const bestN = bestCountForWindow(n);
  // Best differentials: lowest first, ties broken by most recent — matches the
  // index query's `ORDER BY handicap_differential ASC, date DESC`.
  const counting = [...window]
    .sort((a, b) =>
      (Number(a.handicap_differential) - Number(b.handicap_differential)) ||
      (parseLocalDate(b.date).getTime() - parseLocalDate(a.date).getTime()))
    .slice(0, bestN);
  return {
    windowIds: new Set(window.map((r) => r.score_id)),
    countingIds: new Set(counting.map((r) => r.score_id)),
    startDate: n ? window[n - 1].date : null,
    endDate: n ? window[0].date : null,
    windowCount: n,
    countingCount: bestN,
  };
};

const s: Record<string, React.CSSProperties> = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px 16px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  // Centered content column that fills small screens and caps on large ones.
  inner: {
    maxWidth: 1120,
    margin: '0 auto',
    width: '100%',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 24px',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    border: '1px solid #e2e8f0',
    marginBottom: 24,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  },
  cardBody: { padding: '16px 20px' },
  label: { fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 4, display: 'block' },
  select: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 14,
    color: '#1e293b',
    background: '#fff',
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
    maxWidth: 280,
  },
  selectDisabled: {
    background: '#f1f5f9',
    color: '#94a3b8',
    cursor: 'not-allowed',
  },
  simRow: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginBottom: 20 },
  simField: { display: 'flex', flexDirection: 'column', minWidth: 200 },
  statPill: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
  },
  teeboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
    marginTop: 16,
    maxWidth: 400,
  },
  teeboxStat: {
    background: '#f8fafc',
    borderRadius: 8,
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
  },
  teeboxStatLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 },
  teeboxStatVal: { fontSize: 18, fontWeight: 700, color: '#1e293b' },
  expandBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    width: '100%',
    textAlign: 'left',
    fontSize: 14,
    color: '#1e293b',
    fontWeight: 600,
  } as React.CSSProperties,
  playerRow: {
    borderBottom: '1px solid #f1f5f9',
    background: '#fff',
  },
  playerRowExpanded: {
    borderBottom: '1px solid #f1f5f9',
    background: '#f8fafc',
  },
  courseAccordion: {
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    marginBottom: 12,
    overflow: 'hidden',
  },
  courseHeader: {
    padding: '14px 18px',
    background: '#f8fafc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontWeight: 700,
    color: '#1e293b',
    fontSize: 15,
    userSelect: 'none',
  } as React.CSSProperties,
  teeboxAccordion: {
    borderTop: '1px solid #e2e8f0',
  },
  teeboxHeader: {
    padding: '10px 24px',
    background: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 500,
    color: '#475569',
    fontSize: 14,
    borderBottom: '1px solid #f1f5f9',
    userSelect: 'none',
  } as React.CSSProperties,
  avgBadge: {
    marginLeft: 'auto',
    background: '#dbeafe',
    color: '#1d4ed8',
    borderRadius: 20,
    padding: '2px 10px',
    fontSize: 13,
    fontWeight: 700,
  },
  // Player name + handicap-window date-range pill in the round-history header.
  playerNameWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
  },
  rangePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    color: '#4338ca',
    border: '1px solid #c7d2fe',
    borderRadius: 20,
    padding: '2px 10px',
    fontSize: 11.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    letterSpacing: '0.01em',
  } as React.CSSProperties,
  // Highlight + legend for rounds inside the scoring window.
  windowRow: { background: '#eff6ff' },
  windowLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    fontSize: 12,
    color: '#475569',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    lineHeight: 1.4,
  },
  windowSwatch: {
    display: 'inline-block',
    width: 14,
    height: 14,
    borderRadius: 4,
    background: '#eff6ff',
    border: '1px solid #6366f1',
    borderLeft: '3px solid #6366f1',
    flexShrink: 0,
  },
};

const Handicaps = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [players, setPlayers] = useState<PlayerData[]>([]);
  const [expandedPlayers, setExpandedPlayers] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [handicapIndexData, setHandicapIndexData] = useState<HandicapIndex[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [teeboxes, setTeeboxes] = useState<Teebox[]>([]);
  const [selectedTeeboxId, setSelectedTeeboxId] = useState<string>('');
  const [selectedTeebox, setSelectedTeebox] = useState<Teebox | null>(null);
  const [averageScores, setAverageScores] = useState<AverageScore[]>([]);
  const [expandedCourses, setExpandedCourses] = useState<Record<string, boolean>>({});
  const [expandedTeeboxes, setExpandedTeeboxes] = useState<Record<string, boolean>>({});
  const groupedScores = groupByCourseAndTeebox(averageScores);

  const fetchAll = async () => {
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const [hcpRes, handicapsRes, coursesRes, avgRes, facilitiesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/handicap-index`, { headers }),
        axios.get(`${API_BASE_URL}/handicaps`, { headers }),
        axios.get(`${API_BASE_URL}/courses`, { headers }),
        axios.get(`${API_BASE_URL}/course-performance`, { headers }),
        axios.get(`${API_BASE_URL}/facilities`, { headers }),
      ]);
      setHandicapIndexData(hcpRes.data);
      setPlayers(handicapsRes.data);
      setCourses(coursesRes.data);
      setAverageScores(avgRes.data);
      setFacilities(facilitiesRes.data);
    } catch (err) {
      console.error('Error fetching handicap data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeeboxes = async (courseId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const res = await axios.get(`${API_BASE_URL}/teeboxes?id=${courseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeeboxes(res.data);
    } catch {
      setTeeboxes([]);
    }
  };

  const fetchTeeboxDetails = async (teeboxId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const res = await axios.get(`${API_BASE_URL}/teebox?id=${teeboxId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedTeebox(res.data);
    } catch {
      console.error('Error fetching teebox');
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const uniqueCourses = courses.reduce<Course[]>((acc, c) => {
    if (!acc.find((x) => x.course_id === c.course_id)) acc.push(c);
    return acc;
  }, []);

  // Facility → Course → Teebox cascade. Only offer facilities that actually
  // have a (selectable) course, and only list the courses that belong to the
  // chosen facility, so each step narrows the next.
  const facilityIdsWithCourses = new Set(uniqueCourses.map((c) => String(c.facility_id)));
  const facilitiesWithCourses = facilities.filter((f) => facilityIdsWithCourses.has(String(f.facility_id)));
  const coursesForFacility = selectedFacilityId
    ? uniqueCourses.filter((c) => String(c.facility_id) === selectedFacilityId)
    : [];

  // Per-player scoring window (which rounds feed the index + its date span),
  // derived from the round history so the highlights and the displayed range
  // always agree. Keyed by player_id for the Handicap Index table to reuse.
  const windowByPlayer = new Map<number, HandicapWindow>();
  players.forEach((p) => windowByPlayer.set(p.player_id, computeHandicapWindow(p.rounds)));

  if (loading) return <div style={{ padding: 40, color: '#64748b', textAlign: 'center' }}>Loading handicaps…</div>;

  return (
    <div style={s.page}>
      <div style={s.inner}>
      <h1 style={s.pageTitle}>Handicaps</h1>

      {/* Handicap Index Card */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={{ fontSize: 20 }}>📋</span>
          <h2 style={s.cardTitle}>Current Handicap Index</h2>
        </div>
        <div style={s.cardBody}>
          {/* Course simulator */}
          <p style={{ margin: '0 0 14px', fontSize: 14, color: '#64748b' }}>
            Simulate a course handicap based on current index:
          </p>
          <div style={s.simRow}>
            <div style={s.simField}>
              <label style={s.label}>Facility</label>
              <select
                style={s.select}
                value={selectedFacilityId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedFacilityId(id);
                  // Reset everything downstream so the cascade starts clean.
                  setSelectedCourseId('');
                  setSelectedTeeboxId('');
                  setSelectedTeebox(null);
                  setTeeboxes([]);
                }}
              >
                <option value="">— Select a facility —</option>
                {facilitiesWithCourses.map((f) => (
                  <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>
                ))}
              </select>
            </div>

            <div style={s.simField}>
              <label style={s.label}>Course</label>
              <select
                style={{ ...s.select, ...(selectedFacilityId ? {} : s.selectDisabled) }}
                value={selectedCourseId}
                disabled={!selectedFacilityId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedCourseId(id);
                  setSelectedTeeboxId('');
                  setSelectedTeebox(null);
                  setTeeboxes([]);
                  if (id) fetchTeeboxes(id);
                }}
              >
                <option value="">{selectedFacilityId ? '— Select a course —' : '— Select a facility first —'}</option>
                {coursesForFacility.map((c) => (
                  <option key={c.course_id} value={c.course_id}>{c.course_name}</option>
                ))}
              </select>
            </div>

            {teeboxes.length > 0 && (
              <div style={s.simField}>
                <label style={s.label}>Teebox</label>
                <select
                  style={s.select}
                  value={selectedTeeboxId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedTeeboxId(id);
                    if (id) fetchTeeboxDetails(id);
                    else setSelectedTeebox(null);
                  }}
                >
                  <option value="">— Select a teebox —</option>
                  {teeboxes.map((tb) => (
                    <option key={tb.course_teebox_id} value={tb.course_teebox_id}>{tb.course_teebox_name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {selectedTeebox && (
            <div style={s.teeboxGrid}>
              {[
                { label: 'Slope', val: selectedTeebox.course_teebox_slope },
                { label: 'Rating', val: selectedTeebox.course_teebox_rating },
                { label: 'Yards', val: selectedTeebox.course_teebox_yards },
                { label: 'Par', val: selectedTeebox.course_teebox_par },
              ].map(({ label, val }) => (
                <div key={label} style={s.teeboxStat}>
                  <div style={s.teeboxStatLabel}>{label}</div>
                  <div style={s.teeboxStatVal}>{val}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <FitTable>
          <table className="tbl">
            <thead>
              <tr>
                <th>Player</th>
                <th className="r">HCP Index</th>
                {selectedTeebox && <th className="r">Course Handicap</th>}
              </tr>
            </thead>
            <tbody>
              {[...handicapIndexData]
                .sort((a, b) => a.handicap_index - b.handicap_index)
                .map((player) => {
                  const courseHcp = selectedTeebox
                    ? Math.round((player.handicap_index * selectedTeebox.course_teebox_slope) / 113)
                    : null;
                  return (
                    <tr key={player.player_id}>
                      <td className="tbl-name" style={{ fontWeight: 600, color: '#1e293b' }}>
                        <PlayerName first={player.first_name} last={player.last_name} />
                      </td>
                      <td className="r" style={{ fontWeight: 700, color: '#1e293b' }}>
                        {player.handicap_index}
                      </td>
                      {selectedTeebox && (
                        <td className="r" style={{ fontWeight: 700, fontSize: '1.15em' }}>
                          {courseHcp}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </FitTable>
      </div>

      {/* Rounds History */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={{ fontSize: 20 }}>🗂️</span>
          <h2 style={s.cardTitle}>Round History</h2>
        </div>
        {players.map((player) => {
          const expanded = expandedPlayers[player.player_id] || false;
          const win = windowByPlayer.get(player.player_id) ?? computeHandicapWindow(player.rounds);
          return (
            <div key={player.player_id} style={expanded ? s.playerRowExpanded : s.playerRow}>
              <button
                style={s.expandBtn}
                onClick={() => setExpandedPlayers((prev) => ({ ...prev, [player.player_id]: !expanded }))}
              >
                <span style={{ color: '#94a3b8', fontSize: 12, flexShrink: 0 }}>{expanded ? '▼' : '▶'}</span>
                <span style={s.playerNameWrap}>
                  <span>{player.player_name}</span>
                  {win.windowCount > 0 && (
                    <span
                      style={s.rangePill}
                      title={`Date range of the ${win.windowCount} round${win.windowCount !== 1 ? 's' : ''} used for this handicap index`}
                    >
                      <span aria-hidden style={{ fontSize: 11 }}>📅</span>
                      {fmtShortDate(win.startDate)} – {fmtShortDate(win.endDate)}
                    </span>
                  )}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8', fontWeight: 400, flexShrink: 0 }}>
                  {player.rounds.length} round{player.rounds.length !== 1 ? 's' : ''}
                </span>
              </button>
              {expanded && (
                <div style={{ borderTop: '1px solid #e2e8f0' }}>
                  {win.windowCount > 0 && (
                    <div style={s.windowLegend}>
                      <span style={s.windowSwatch} />
                      <span>
                        Highlighted rows are the <strong>{win.windowCount}</strong> most recent 18-hole round{win.windowCount !== 1 ? 's' : ''} in this handicap window
                        {win.countingCount > 0 && (
                          <> — <span style={{ color: '#16a34a', fontWeight: 800 }}>✓</span> marks the best {win.countingCount} that {win.countingCount !== 1 ? 'are' : 'is'} averaged into the index</>
                        )}.
                      </span>
                    </div>
                  )}
                  <FitTable>
                    <table className="tbl tbl--sm">
                      <thead>
                        <tr>
                          <th className="c col-fit" style={{ padding: '0.6em 0.3em' }} title="Counts toward the handicap index" />
                          <th>Date</th>
                          <th>Course</th>
                          <th className="c">Par</th>
                          <th>Teebox</th>
                          <th className="r">Slope</th>
                          <th className="r">Rating</th>
                          <th className="r">Score</th>
                          <th className="r">Differential</th>
                        </tr>
                      </thead>
                      <tbody>
                        {player.rounds.map((round) => {
                          const inWindow = win.windowIds.has(round.score_id);
                          const counts = win.countingIds.has(round.score_id);
                          return (
                            <tr key={round.score_id} style={inWindow ? s.windowRow : undefined}>
                              <td className="c" style={{ padding: '0.6em 0.3em', borderLeft: inWindow ? '3px solid #6366f1' : '3px solid transparent' }}>
                                {counts
                                  ? <span title="Counts toward the index (best differential)" style={{ color: '#16a34a', fontWeight: 800 }}>✓</span>
                                  : inWindow
                                    ? <span title="In the 20-round window" style={{ color: '#c7d2fe', fontWeight: 700 }}>•</span>
                                    : null}
                              </td>
                              <td>{fmtShortDate(round.date)}</td>
                              <td>{round.course_name}</td>
                              <td className="c">{round.course_teebox_par}</td>
                              <td>{round.course_teebox_name}</td>
                              <td className="r">{round.course_teebox_slope}</td>
                              <td className="r">{round.course_teebox_rating}</td>
                              <td className="r" style={{ fontWeight: 600 }}>{round.player_score}</td>
                              <td className="r" style={{ fontWeight: 700, color: Number(round.handicap_differential) < 0 ? '#15803d' : '#374151' }}>
                                {round.handicap_differential}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </FitTable>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Average Scores by Course & Teebox */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={{ fontSize: 20 }}>📈</span>
          <h2 style={s.cardTitle}>Average Scores by Course & Teebox</h2>
        </div>
        <div style={s.cardBody}>
          {Object.entries(groupedScores).map(([courseName, teeboxMap]) => {
            const isCourseOpen = expandedCourses[courseName] || false;
            return (
              <div key={courseName} style={s.courseAccordion}>
                <div
                  style={s.courseHeader}
                  onClick={() => setExpandedCourses((prev) => ({ ...prev, [courseName]: !isCourseOpen }))}
                >
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>{isCourseOpen ? '▼' : '▶'}</span>
                  {courseName}
                </div>
                {isCourseOpen && (
                  <div style={s.teeboxAccordion}>
                    {Object.entries(teeboxMap).map(([teeboxName, tbPlayers]) => {
                      const teeboxKey = `${courseName}-${teeboxName}`;
                      const isTeeboxOpen = expandedTeeboxes[teeboxKey] || false;
                      const avg = calculateTeeboxAverage(tbPlayers);
                      return (
                        <div key={teeboxKey}>
                          <div
                            style={s.teeboxHeader}
                            onClick={() => setExpandedTeeboxes((prev) => ({ ...prev, [teeboxKey]: !isTeeboxOpen }))}
                          >
                            <span style={{ color: '#94a3b8', fontSize: 11 }}>{isTeeboxOpen ? '▼' : '▶'}</span>
                            <span>{teeboxName}</span>
                            <span style={s.avgBadge}>{avg}</span>
                          </div>
                          {isTeeboxOpen && (
                            <FitTable>
                              <table className="tbl tbl--sm">
                                <thead>
                                  <tr>
                                    <th>Player</th>
                                    <th className="r">Rounds</th>
                                    <th className="r">Avg Score</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...tbPlayers]
                                    .sort((a, b) => a.average_score - b.average_score)
                                    .map((p) => (
                                      <tr key={`${p.player_id}-${p.course_id}-${p.course_teebox_id}`}>
                                        <td className="tbl-name" style={{ fontWeight: 500 }}><PlayerName full={p.player_name} /></td>
                                        <td className="r" style={{ color: '#64748b' }}>{p.total_rounds}</td>
                                        <td className="r" style={{ fontWeight: 700, color: '#1e293b' }}>
                                          {p.average_score}
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </FitTable>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {Object.keys(groupedScores).length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 14 }}>
              No average score data available.
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Handicaps;
